// Exact rationals for maturity scores (docs/maturity/02: no floating-point misclassification at 0, 50 or 100).
// Values are percentages, so 125/2 means 62.5%.

export interface Fraction {
  readonly n: bigint;
  /** Always positive; the fraction is kept reduced. */
  readonly d: bigint;
}

const abs = (v: bigint) => (v < 0n ? -v : v);
function gcd(a: bigint, b: bigint): bigint {
  a = abs(a);
  b = abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1n;
}

export function fraction(n: bigint, d: bigint = 1n): Fraction {
  if (d === 0n) throw new RangeError("zero denominator");
  if (d < 0n) [n, d] = [-n, -d];
  const g = gcd(n, d);
  return { n: n / g, d: d / g };
}

export const add = (a: Fraction, b: Fraction) => fraction(a.n * b.d + b.n * a.d, a.d * b.d);
export const sub = (a: Fraction, b: Fraction) => fraction(a.n * b.d - b.n * a.d, a.d * b.d);
export const divInt = (a: Fraction, k: number | bigint) => fraction(a.n, a.d * BigInt(k));
/** Negative, zero or positive, like a comparator. */
export const cmp = (a: Fraction, b: Fraction) => {
  const l = a.n * b.d;
  const r = b.n * a.d;
  return l < r ? -1 : l > r ? 1 : 0;
};
export const cmpInt = (a: Fraction, k: number) => cmp(a, fraction(BigInt(k)));
export const toNumber = (a: Fraction) => Number(a.n) / Number(a.d);
export const toStrings = (a: Fraction): [string, string] => [a.n.toString(), a.d.toString()];
export const fromStrings = (v: readonly [string, string]) => fraction(BigInt(v[0]), BigInt(v[1]));

/** Equal arithmetic mean; null when the list is empty or any item is missing (a parent of an incomplete child). */
export function mean(items: (Fraction | null)[]): Fraction | null {
  if (!items.length || items.some((x) => x === null)) return null;
  return divInt((items as Fraction[]).reduce(add, fraction(0n)), items.length);
}
